import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import Loader from '../components/Loader';
import ConfirmDialog from '../components/ConfirmDialog';
import SearchableSelect from '../components/SearchableSelect';
import ViewToggle from '../components/ViewToggle';
import { formatDate, toInputDate } from '../utils/date';
import { cardHeaderHue } from '../utils/cardTheme';
import { useViewPreference } from '../utils/viewPreference';
import { PAGE_SIZE_OPTIONS, buildPageList } from '../utils/pagination';

const EMPTY_FORM = {
  intake_code: '', course_code: '', soc_date: '', eoc_date: '', intake_type: '',
  capacity: '', min_attendance_rate: '', min_assessment_result: '', is_active: 1,
};
const SORT_COLUMNS = [
  { key: 'course_name', label: 'Course' },
  { key: 'intake_code', label: 'Intake Code' },
  { key: 'soc_date', label: 'SOC' },
  { key: 'eoc_date', label: 'EOC' },
  { key: 'is_active', label: 'Status' },
  { key: 'student_count', label: 'Student Count' },
];
const DAY_MS = 24 * 60 * 60 * 1000;

function compareValues(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' });
}

function validateThresholds(minAttendanceRate, minAssessmentResult) {
  for (const [label, value] of [['min_attendance_rate', minAttendanceRate], ['min_assessment_result', minAssessmentResult]]) {
    if (value === undefined || value === null || value === '') continue;
    const n = Number(value);
    if (!Number.isInteger(n) || n < 0 || n > 100) return `${label} must be a whole number between 0 and 100`;
  }
  return null;
}

export default function IntakeMasterPage() {
  const { show } = useToast();
  const [intakes, setIntakes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [perms, setPerms] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [courseFilter, setCourseFilter] = useState('');
  const [timingFilter, setTimingFilter] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [view, setView] = useViewPreference('intakes');
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [modulesRow, setModulesRow] = useState(null);
  const [modulesChecked, setModulesChecked] = useState(() => new Set());
  const [modulesSaving, setModulesSaving] = useState(false);
  const [modulesSearch, setModulesSearch] = useState('');

  const [transferRow, setTransferRow] = useState(null);
  const [transferForm, setTransferForm] = useState({ to_intake_code: '', transfer_date: '' });
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkChecked, setBulkChecked] = useState(() => new Set());
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkSearch, setBulkSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get('/records/intakes'),
      api.get('/courses'),
      api.get('/users/me/permissions'),
    ])
      .then(([intakesRes, coursesRes, permsRes]) => {
        setIntakes(intakesRes.data.data);
        setCourses(coursesRes.data.data);
        setPerms(permsRes.data.data.intake_master || { view: false, create: false, update: false, delete: false });
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load intakes'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, statusFilter, courseFilter, timingFilter, pageSize]);

  const activeCourses = useMemo(() => courses.filter((c) => c.status === 'Active'), [courses]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <i className="fa-solid fa-sort" style={{ color: 'var(--slate-lt)', marginLeft: '.35rem' }} />;
    return <i className={`fa-solid ${sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down'}`} style={{ color: 'var(--ocean)', marginLeft: '.35rem' }} />;
  };

  const filteredSorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    const now = Date.now();
    let list = intakes.filter((i) => {
      if (term && !i.intake_code.toLowerCase().includes(term)) return false;
      if (statusFilter === 'active' && !i.is_active) return false;
      if (statusFilter === 'inactive' && i.is_active) return false;
      if (courseFilter && i.course_code !== courseFilter) return false;
      if (timingFilter === 'soon') {
        if (!i.soc_date) return false;
        const soc = new Date(i.soc_date).getTime();
        const diffDays = (soc - now) / DAY_MS;
        if (diffDays < 0 || diffDays > 30) return false;
      }
      if (timingFilter === 'ongoing') {
        if (!i.soc_date || !i.eoc_date) return false;
        const soc = new Date(i.soc_date).getTime();
        const eoc = new Date(i.eoc_date).getTime();
        if (now < soc || now > eoc) return false;
      }
      if (timingFilter === 'ended') {
        if (!i.eoc_date) return false;
        const eoc = new Date(i.eoc_date).getTime();
        if (now <= eoc) return false;
      }
      return true;
    });
    if (sortKey) {
      const dir = sortDir === 'asc' ? 1 : -1;
      list = list.slice().sort((a, b) => compareValues(a[sortKey], b[sortKey]) * dir);
    }
    return list;
  }, [intakes, search, statusFilter, courseFilter, timingFilter, sortKey, sortDir]);

  const total = filteredSorted.length;
  const effectivePageSize = pageSize === 'All' ? total || 1 : pageSize;
  const totalPages = Math.max(1, Math.ceil(total / effectivePageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    if (pageSize === 'All') return filteredSorted;
    const start = (currentPage - 1) * effectivePageSize;
    return filteredSorted.slice(start, start + effectivePageSize);
  }, [filteredSorted, currentPage, effectivePageSize, pageSize]);

  const rangeStart = total === 0 ? 0 : (pageSize === 'All' ? 1 : (currentPage - 1) * effectivePageSize + 1);
  const rangeEnd = pageSize === 'All' ? total : Math.min(currentPage * effectivePageSize, total);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalMode('create');
  };

  const openEdit = (row) => {
    setForm({
      intake_code: row.intake_code,
      course_code: row.course_code || '',
      soc_date: toInputDate(row.soc_date),
      eoc_date: toInputDate(row.eoc_date),
      intake_type: row.intake_type || '',
      capacity: row.capacity ?? '',
      min_attendance_rate: row.min_attendance_rate ?? '',
      min_assessment_result: row.min_assessment_result ?? '',
      is_active: row.is_active ? 1 : 0,
    });
    setFormError(null);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const toggleActive = async (row) => {
    try {
      await api.put(`/records/intakes/${row.id}`, {
        intake_code: row.intake_code,
        course_code: row.course_code || null,
        soc_date: toInputDate(row.soc_date),
        eoc_date: toInputDate(row.eoc_date),
        intake_type: row.intake_type || null,
        capacity: row.capacity,
        min_attendance_rate: row.min_attendance_rate,
        min_assessment_result: row.min_assessment_result,
        is_active: row.is_active ? 0 : 1,
      });
      show(`Intake ${row.is_active ? 'disabled' : 'enabled'}`, 'success');
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  const hasEnrolledStudents = (row) => !!(row && (row.student_count > 0 || row.occupied_count > 0));
  // Mirrors the backend's delete guard exactly — ANY student_courses row ever
  // linked to this intake (pending/active/completed/dropped/transferred),
  // not just currently-active ones — so Delete is disabled up front instead
  // of failing with a toast after the click.
  const hasAnyStudents = (row) => !!(row && row.total_student_count > 0);

  const editingRow = useMemo(() => {
    if (modalMode !== 'edit') return null;
    return intakes.find((i) => i.intake_code === form.intake_code) || null;
  }, [modalMode, form.intake_code, intakes]);

  const isDuplicateCode = useMemo(() => {
    if (modalMode !== 'create') return false;
    const code = form.intake_code.trim().toLowerCase();
    if (!code) return false;
    return intakes.some((i) => i.intake_code.toLowerCase() === code);
  }, [modalMode, form.intake_code, intakes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const codeBlank = !form.intake_code || !form.intake_code.trim();
    const socBlank = !form.soc_date;
    const eocBlank = !form.eoc_date;
    if (codeBlank || socBlank || eocBlank) {
      setFormError('intake_code, soc_date and eoc_date are required.');
      return;
    }
    if (modalMode === 'create' && isDuplicateCode) {
      setFormError('Intake code already exists.');
      return;
    }
    const thresholdError = validateThresholds(form.min_attendance_rate, form.min_assessment_result);
    if (thresholdError) {
      setFormError(thresholdError);
      return;
    }
    setFormError(null);
    setSubmitting(true);
    const payload = {
      course_code: form.course_code || null,
      soc_date: form.soc_date,
      eoc_date: form.eoc_date,
      intake_type: form.intake_type.trim() || null,
      capacity: form.capacity === '' ? 50 : Number(form.capacity),
      min_attendance_rate: form.min_attendance_rate === '' ? null : Number(form.min_attendance_rate),
      min_assessment_result: form.min_assessment_result === '' ? null : Number(form.min_assessment_result),
      is_active: Number(form.is_active),
    };
    try {
      if (modalMode === 'create') {
        await api.post('/records/intakes', { ...payload, intake_code: form.intake_code.trim() });
        show('Intake created', 'success');
      } else {
        await api.put(`/records/intakes/${editingRow.id}`, { ...payload, intake_code: form.intake_code.trim() });
        show('Intake updated', 'success');
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || `Failed to ${modalMode === 'create' ? 'create' : 'update'} intake`);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    const row = deleteTarget;
    setDeleting(true);
    try {
      await api.delete(`/records/intakes/${row.id}`);
      show('Intake deleted', 'success');
      setDeleteTarget(null);
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Failed to delete intake', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setCourseFilter('');
    setTimingFilter('');
    setSelectedIds(new Set());
    setSortKey(null);
    setSortDir('asc');
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allOnPageSelected = pageRows.length > 0 && pageRows.every((row) => selectedIds.has(row.id));
  const someOnPageSelected = !allOnPageSelected && pageRows.some((row) => selectedIds.has(row.id));
  const selectAllRef = useRef(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someOnPageSelected;
  }, [someOnPageSelected]);

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        pageRows.forEach((row) => next.delete(row.id));
      } else {
        pageRows.forEach((row) => next.add(row.id));
      }
      return next;
    });
  };

  // --- Manage modules for a single intake ---
  const openModules = async (row) => {
    setModulesRow(row);
    setModulesChecked(new Set());
    setModulesSearch('');
    try {
      const [allModsRes, assignedRes] = await Promise.all([
        modules.length ? Promise.resolve({ data: { data: modules } }) : api.get('/modules'),
        api.get(`/records/intakes/${row.id}/modules`),
      ]);
      if (!modules.length) setModules(allModsRes.data.data);
      setModulesChecked(new Set(assignedRes.data.data.map((m) => m.module_code)));
    } catch (err) {
      show(err.response?.data?.message || 'Failed to load modules', 'error');
      setModulesRow(null);
    }
  };

  const toggleModuleChecked = (code) => {
    setModulesChecked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const filteredModulesList = useMemo(() => {
    const term = modulesSearch.trim().toLowerCase();
    if (!term) return modules;
    return modules.filter((m) => m.module_name.toLowerCase().includes(term) || m.module_code.toLowerCase().includes(term));
  }, [modules, modulesSearch]);

  const allFilteredModulesChecked = filteredModulesList.length > 0 && filteredModulesList.every((m) => modulesChecked.has(m.module_code));
  const checkedModuleObjs = useMemo(() => modules.filter((m) => modulesChecked.has(m.module_code)), [modules, modulesChecked]);

  const toggleAllFilteredModules = () => {
    setModulesChecked((prev) => {
      const next = new Set(prev);
      if (allFilteredModulesChecked) {
        filteredModulesList.forEach((m) => next.delete(m.module_code));
      } else {
        filteredModulesList.forEach((m) => next.add(m.module_code));
      }
      return next;
    });
  };

  const saveModules = async () => {
    setModulesSaving(true);
    try {
      await api.put(`/records/intakes/${modulesRow.id}/modules`, { module_codes: [...modulesChecked] });
      show('Modules updated', 'success');
      setModulesRow(null);
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Failed to update modules', 'error');
    } finally {
      setModulesSaving(false);
    }
  };

  // --- Transfer all active students from one intake to another ---
  const openTransfer = (row) => {
    setTransferRow(row);
    setTransferForm({ to_intake_code: '', transfer_date: new Date().toISOString().slice(0, 10) });
  };

  // Destination can only be an intake that hasn't ended yet — ongoing (SOC
  // already passed, EOC hasn't) or upcoming (SOC still in the future).
  // Excludes the source intake itself and anything whose EOC is already past.
  const transferableIntakes = useMemo(() => {
    if (!transferRow) return [];
    const now = Date.now();
    return intakes.filter((i) => {
      if (i.intake_code === transferRow.intake_code) return false;
      if (!i.eoc_date) return false;
      return new Date(i.eoc_date).getTime() >= now;
    });
  }, [intakes, transferRow]);

  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!transferForm.to_intake_code || !transferForm.transfer_date) {
      show('Destination intake and transfer date are required', 'error');
      return;
    }
    setTransferSubmitting(true);
    try {
      const res = await api.post(`/records/intakes/${transferRow.id}/transfer-all`, transferForm);
      show(`Transferred ${res.data.moved} student(s)`, 'success');
      setTransferRow(null);
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Failed to transfer students', 'error');
    } finally {
      setTransferSubmitting(false);
    }
  };

  // --- Bulk-assign modules to every selected intake ---
  const openBulkAssign = async () => {
    setBulkChecked(new Set());
    setBulkSearch('');
    setBulkModalOpen(true);
    if (!modules.length) {
      try {
        const res = await api.get('/modules');
        setModules(res.data.data);
      } catch (err) {
        show(err.response?.data?.message || 'Failed to load modules', 'error');
      }
    }
  };

  const toggleBulkChecked = (code) => {
    setBulkChecked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const filteredBulkModulesList = useMemo(() => {
    const term = bulkSearch.trim().toLowerCase();
    if (!term) return modules;
    return modules.filter((m) => m.module_name.toLowerCase().includes(term) || m.module_code.toLowerCase().includes(term));
  }, [modules, bulkSearch]);

  const allFilteredBulkChecked = filteredBulkModulesList.length > 0 && filteredBulkModulesList.every((m) => bulkChecked.has(m.module_code));
  const checkedBulkModuleObjs = useMemo(() => modules.filter((m) => bulkChecked.has(m.module_code)), [modules, bulkChecked]);

  const toggleAllFilteredBulkModules = () => {
    setBulkChecked((prev) => {
      const next = new Set(prev);
      if (allFilteredBulkChecked) {
        filteredBulkModulesList.forEach((m) => next.delete(m.module_code));
      } else {
        filteredBulkModulesList.forEach((m) => next.add(m.module_code));
      }
      return next;
    });
  };

  const submitBulkAssign = async () => {
    if (!bulkChecked.size) return;
    setBulkSubmitting(true);
    try {
      await api.post('/records/intakes/modules/bulk-assign', {
        module_codes: [...bulkChecked],
        intake_ids: [...selectedIds],
      });
      show('Modules assigned', 'success');
      setBulkModalOpen(false);
      setSelectedIds(new Set());
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Failed to bulk-assign modules', 'error');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const courseName = useCallback((code) => courses.find((c) => c.course_code === code)?.course_name || null, [courses]);

  if (!loading && !error && perms && !perms.view) {
    return (
      <div>
        <div className="sh-ey">PROGRAMMES · INTAKE MASTER</div>
        <div className="sh-ti">Intake Master</div>
        <div className="alert"><i className="fa-solid fa-lock" /><div className="alert-t">You do not have access to Intake Master.</div></div>
      </div>
    );
  }

  return (
    <div>
      <div className="sh-ey">PROGRAMMES · INTAKE MASTER</div>
      <div className="sh-ti">Intake Master</div>
      <div className="sh-de">Live intake schedule from the backend — start/end dates, capacity, and enrolment thresholds for each course run.</div>

      <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div className="icon-field" style={{ flex: '1 1 220px', minWidth: 180 }}>
          <i className="fa-solid fa-magnifying-glass" />
          <input className="finput" placeholder="Search intake code…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="icon-field">
          <i className="fa-solid fa-filter" />
          <SearchableSelect
            className="finput" style={{ width: 150 }} value={statusFilter} onChange={setStatusFilter}
            placeholder="All Statuses"
            options={[{ value: '', label: 'All Statuses' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
          />
        </div>
        <div className="icon-field">
          <i className="fa-solid fa-book" />
          <SearchableSelect
            className="finput" style={{ width: 190 }} value={courseFilter} onChange={setCourseFilter}
            placeholder="All Courses"
            options={[{ value: '', label: 'All Courses' }, ...activeCourses.map((c) => ({ value: c.course_code, label: c.course_name }))]}
          />
        </div>
        <div className="icon-field">
          <i className="fa-solid fa-clock" />
          <SearchableSelect
            className="finput" style={{ width: 160 }} value={timingFilter} onChange={setTimingFilter}
            placeholder="All Timing"
            options={[
              { value: '', label: 'All Timing' },
              { value: 'soon', label: 'Starting soon' },
              { value: 'ongoing', label: 'Already started' },
              { value: 'ended', label: 'Already ended' },
            ]}
          />
        </div>
        {view === 'tiles' && (
          <div className="icon-field" style={{ flex: '1 1 180px', minWidth: 160 }}>
            <i className="fa-solid fa-arrow-down-a-z" />
            <SearchableSelect
              className="finput" value={sortKey || ''} onChange={(val) => setSortKey(val || null)}
              placeholder="Default order"
              options={[{ value: '', label: 'Default order' }, ...SORT_COLUMNS.map((o) => ({ value: o.key, label: `Sort: ${o.label}` }))]}
            />
          </div>
        )}
        {view === 'tiles' && sortKey && (
          <button type="button" className="btn btn-outline btn-toolbar" title={sortDir === 'asc' ? 'Ascending' : 'Descending'} onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
            <i className={`fa-solid ${sortDir === 'asc' ? 'fa-arrow-up-wide-short' : 'fa-arrow-down-wide-short'}`} />
          </button>
        )}
        <button type="button" className="btn btn-outline btn-toolbar" onClick={clearFilters}><i className="fa-solid fa-arrow-rotate-left" /> Clear</button>
        {perms?.update && selectedIds.size > 0 && (
          <button type="button" className="btn btn-outline btn-toolbar" onClick={openBulkAssign}>
            <i className="fa-solid fa-layer-group" /> Bulk Assign Modules ({selectedIds.size})
          </button>
        )}
        <ViewToggle view={view} onChange={setView} />
        {perms?.create && (
          <button className="btn btn-primary btn-toolbar" style={{ marginLeft: 'auto' }} onClick={openCreate}><i className="fa-solid fa-plus" /> Add Intake</button>
        )}
      </div>

      {loading && <Loader label="Loading intakes…" />}
      {error && !loading && <div className="alert"><i className="fa-solid fa-triangle-exclamation" /><div className="alert-t">{error}</div></div>}

      {!loading && !error && !intakes.length && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          <i className="fa-solid fa-calendar-days" style={{ fontSize: '2rem', color: 'var(--slate-lt)', marginBottom: '.75rem', display: 'block' }} />
          <div style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: '.35rem' }}>No intakes yet</div>
          <div style={{ color: 'var(--slate)', marginBottom: '1rem' }}>Add your first intake to get started.</div>
          {perms?.create && (
            <button className="btn btn-primary" style={{ margin: '0 auto' }} onClick={openCreate}><i className="fa-solid fa-plus" /> Add Intake</button>
          )}
        </div>
      )}

      {!loading && !error && !!intakes.length && !total && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--slate)' }}>
          No intakes match the current search/filters.
        </div>
      )}

      {!loading && !error && !!total && view === 'table' && (
        <div className="card view-fade" key="table">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  {perms?.update && (
                    <th style={{ width: 30 }}>
                      <input
                        type="checkbox"
                        ref={selectAllRef}
                        checked={allOnPageSelected}
                        onChange={toggleSelectAll}
                        aria-label="Select all intakes on this page"
                      />
                    </th>
                  )}
                  <th>#</th>
                  {SORT_COLUMNS.map(({ key, label }) => (
                    <th key={key} style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(key)}>
                      {label}{sortIcon(key)}
                    </th>
                  ))}
                  <th>Capacity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, i) => (
                  <tr key={row.id} style={{ cursor: 'pointer' }} onClick={() => setViewRow(row)}>
                    {perms?.update && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelected(row.id)} />
                      </td>
                    )}
                    <td>{rangeStart + i}</td>
                    <td>{row.course_name || '—'}</td>
                    <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '.8rem', fontWeight: 700, color: 'var(--ocean)' }}>{row.intake_code}</span></td>
                    <td>{formatDate(row.soc_date)}</td>
                    <td>{formatDate(row.eoc_date)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                        {perms?.update ? (
                          <button
                            type="button"
                            className={`toggle ${row.is_active ? 'on' : ''}`}
                            role="switch"
                            aria-checked={!!row.is_active}
                            title={row.is_active ? 'Disable intake' : 'Enable intake'}
                            aria-label={row.is_active ? 'Disable intake' : 'Enable intake'}
                            onClick={() => toggleActive(row)}
                          />
                        ) : null}
                        <span className={`pill ${row.is_active ? 'p-ok' : 'p-draft'}`}>{row.is_active ? 'Active' : 'Inactive'}</span>
                      </div>
                    </td>
                    <td>{row.student_count}</td>
                    <td>{row.occupied_count}/{row.capacity}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {perms?.update && (
                        <button className="btn btn-outline btn-sm" style={{ marginRight: '.3rem' }} onClick={() => openEdit(row)}><i className="fa-solid fa-pen" /></button>
                      )}
                      {perms?.update && (
                        <button className="btn btn-outline btn-sm" style={{ marginRight: '.3rem' }} title="Manage modules" onClick={() => openModules(row)}><i className="fa-solid fa-layer-group" /></button>
                      )}
                      {perms?.update && (
                        <button className="btn btn-outline btn-sm" style={{ marginRight: '.3rem' }} title="Transfer students" onClick={() => openTransfer(row)}><i className="fa-solid fa-right-left" /></button>
                      )}
                      {perms?.delete && (
                        <button
                          className="btn btn-danger btn-sm"
                          disabled={hasAnyStudents(row)}
                          title={hasAnyStudents(row) ? 'Cannot delete — this intake has student records' : undefined}
                          onClick={() => setDeleteTarget(row)}
                        ><i className="fa-solid fa-trash" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && !error && !!total && view === 'tiles' && (
        <div className="g4 view-fade" key="tiles">
          {pageRows.map((row) => (
            <div key={row.id} className={`cc-card ${selectedIds.has(row.id) ? 'cc-card-sel' : ''}`} style={{ cursor: 'pointer' }} onClick={() => setViewRow(row)}>
              <div className="cc-hd cc-hd-tint" style={{ '--h': cardHeaderHue(row.intake_code) }}>
                {perms?.update && (
                  <div
                    className={`cc-hd-check ${selectedIds.has(row.id) ? 'checked' : ''}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleSelected(row.id)}
                      aria-label={`Select intake ${row.intake_code}`}
                    />
                  </div>
                )}
                <div className="cc-em"><i className="fa-solid fa-calendar-days" /></div>
                <div style={{ minWidth: 0 }}>
                  <div className="cc-code">{row.intake_code}</div>
                  <div className="cc-ti">{row.course_name || '—'}</div>
                </div>
              </div>
              <div className="cc-body">
                <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: 'var(--text-sm)', color: 'var(--slate)', marginBottom: '.65rem' }}>
                  <i className="fa-solid fa-calendar-day" style={{ color: 'var(--slate-lt)' }} /> {formatDate(row.soc_date)} → {formatDate(row.eoc_date)}
                </div>
                <div style={{ display: 'flex', gap: '.75rem' }}>
                  <div style={{ flex: 1, textAlign: 'center', background: 'var(--cloud)', border: '1px solid var(--border-soft)', borderRadius: 8, padding: '.5rem' }}>
                    <div style={{ fontSize: 'var(--text-lg)', fontWeight: 800, color: 'var(--ink)' }}>{row.occupied_count}/{row.capacity}</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)' }}>Capacity</div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center', background: 'var(--cloud)', border: '1px solid var(--border-soft)', borderRadius: 8, padding: '.5rem' }}>
                    <div style={{ fontSize: 'var(--text-lg)', fontWeight: 800, color: 'var(--ink)' }}>{row.student_count}</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)' }}>Students</div>
                  </div>
                </div>
              </div>
              <div className="cc-foot">
                <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }} onClick={(e) => e.stopPropagation()}>
                  {perms?.update ? (
                    <button
                      type="button"
                      className={`toggle ${row.is_active ? 'on' : ''}`}
                      role="switch"
                      aria-checked={!!row.is_active}
                      title={row.is_active ? 'Disable intake' : 'Enable intake'}
                      aria-label={row.is_active ? 'Disable intake' : 'Enable intake'}
                      onClick={() => toggleActive(row)}
                    />
                  ) : null}
                  <span className={`pill ${row.is_active ? 'p-ok' : 'p-draft'}`}>{row.is_active ? 'Active' : 'Inactive'}</span>
                </div>
                <div style={{ display: 'flex', gap: '.4rem' }} onClick={(e) => e.stopPropagation()}>
                  {perms?.update && (
                    <button className="btn btn-outline btn-sm" title="Edit intake" aria-label="Edit intake" onClick={() => openEdit(row)}><i className="fa-solid fa-pen" /></button>
                  )}
                  {perms?.update && (
                    <button className="btn btn-outline btn-sm" title="Manage modules" aria-label="Manage modules" onClick={() => openModules(row)}><i className="fa-solid fa-layer-group" /></button>
                  )}
                  {perms?.update && (
                    <button className="btn btn-outline btn-sm" title="Transfer students" aria-label="Transfer students" onClick={() => openTransfer(row)}><i className="fa-solid fa-right-left" /></button>
                  )}
                  {perms?.delete && (
                    <button
                      className="btn btn-danger btn-sm"
                      disabled={hasAnyStudents(row)}
                      aria-label={hasAnyStudents(row) ? 'Cannot delete — this intake has student records' : 'Delete intake'}
                      title={hasAnyStudents(row) ? 'Cannot delete — this intake has student records' : 'Delete intake'}
                      onClick={() => setDeleteTarget(row)}
                    ><i className="fa-solid fa-trash" /></button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && !!total && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '1.3rem', paddingTop: '1rem', borderTop: '1px solid var(--border-soft)' }}>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--slate)' }}>
            Showing {rangeStart}–{rangeEnd} of {total}
          </div>
          <div className="icon-field" style={{ marginLeft: 'auto' }}>
            <SearchableSelect
              className="finput" style={{ width: 110 }} value={pageSize}
              onChange={(val) => setPageSize(val === 'All' ? 'All' : Number(val))}
              options={PAGE_SIZE_OPTIONS.map((opt) => ({ value: opt, label: String(opt) }))}
            />
          </div>
          {pageSize !== 'All' && totalPages > 1 && (
            <div style={{ display: 'flex', gap: '.3rem', alignItems: 'center' }}>
              <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === 1} onClick={() => setPage(1)}><i className="fa-solid fa-angles-left" /></button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === 1} onClick={() => setPage((p) => p - 1)}><i className="fa-solid fa-angle-left" /></button>
              {buildPageList(currentPage, totalPages).map((p, idx) => (
                p === '…'
                  ? <span key={`e${idx}`} style={{ padding: '0 .3rem', color: 'var(--slate-lt)' }}>…</span>
                  : (
                    <button
                      key={p}
                      type="button"
                      className={`btn btn-sm ${p === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  )
              ))}
              <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === totalPages} onClick={() => setPage((p) => p + 1)}><i className="fa-solid fa-angle-right" /></button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}><i className="fa-solid fa-angles-right" /></button>
            </div>
          )}
        </div>
      )}

      {modalMode && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={closeModal}>
          <form className="card modal" style={{ width: 520, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <div className="modal-header">
              <div className="modal-title">{modalMode === 'create' ? 'Add Intake' : 'Edit Intake'}</div>
              <button type="button" className="modal-close" onClick={closeModal} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
            {formError && <div className="lc-err">{formError}</div>}
            <div className="fg fg-tight">
              <label className="fl">Intake Code *</label>
              <input
                className="finput"
                required
                disabled={modalMode === 'edit' && hasEnrolledStudents(editingRow)}
                value={form.intake_code}
                onChange={(e) => setForm((f) => ({ ...f, intake_code: e.target.value }))}
              />
              {modalMode === 'edit' && hasEnrolledStudents(editingRow) && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)', marginTop: '.25rem' }}>Intake code cannot be changed — students are enrolled</div>
              )}
              {modalMode === 'create' && isDuplicateCode && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--red)', marginTop: '.25rem' }}>Intake code already exists.</div>
              )}
            </div>
            <div className="fg fg-tight">
              <label className="fl">Course</label>
              <SearchableSelect
                className="finput" value={form.course_code} onChange={(val) => setForm((f) => ({ ...f, course_code: val }))}
                placeholder="None"
                options={[{ value: '', label: 'None' }, ...activeCourses.map((c) => ({ value: c.course_code, label: c.course_name }))]}
              />
            </div>
            <div style={{ display: 'flex', gap: '.8rem' }}>
              <div className="fg fg-tight" style={{ flex: 1 }}><label className="fl">Start of Course *</label><input className="finput" type="date" required value={form.soc_date} onChange={(e) => setForm((f) => ({ ...f, soc_date: e.target.value }))} /></div>
              <div className="fg fg-tight" style={{ flex: 1 }}><label className="fl">End of Course *</label><input className="finput" type="date" required value={form.eoc_date} onChange={(e) => setForm((f) => ({ ...f, eoc_date: e.target.value }))} /></div>
            </div>
            <div style={{ display: 'flex', gap: '.8rem' }}>
              <div className="fg fg-tight" style={{ flex: 1 }}><label className="fl">Intake Type</label><input className="finput" value={form.intake_type} onChange={(e) => setForm((f) => ({ ...f, intake_type: e.target.value }))} placeholder="e.g. FT / PT" /></div>
              <div className="fg fg-tight" style={{ flex: 1 }}><label className="fl">Capacity</label><input className="finput" type="number" min="0" placeholder="50" value={form.capacity} onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))} /></div>
            </div>
            <div style={{ display: 'flex', gap: '.8rem' }}>
              <div className="fg fg-tight" style={{ flex: 1 }}><label className="fl">Min Attendance Rate (%)</label><input className="finput" type="number" value={form.min_attendance_rate} onChange={(e) => setForm((f) => ({ ...f, min_attendance_rate: e.target.value }))} /></div>
              <div className="fg fg-tight" style={{ flex: 1 }}><label className="fl">Min Assessment Result (%)</label><input className="finput" type="number" value={form.min_assessment_result} onChange={(e) => setForm((f) => ({ ...f, min_assessment_result: e.target.value }))} /></div>
            </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={closeModal}><i className="fa-solid fa-xmark" /> Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting || (modalMode === 'create' && isDuplicateCode)}>
                <i className={`fa-solid ${submitting ? 'fa-spinner fa-spin' : 'fa-check'}`} />
                {submitting ? 'Saving…' : modalMode === 'create' ? 'Create Intake' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {viewRow && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setViewRow(null)}>
          <div className="card modal" style={{ width: 520, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
            <div className="vw-hd">
              <div className="vw-em"><i className="fa-solid fa-calendar-days" /></div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 'var(--text-xs)', color: 'var(--ocean)', letterSpacing: '.04em' }}>{viewRow.intake_code}</div>
                <div style={{ fontWeight: 700, fontSize: 'var(--text-lg)', color: 'var(--ink)' }}>{viewRow.course_name || courseName(viewRow.course_code) || '—'}</div>
              </div>
              <span className={`pill ${viewRow.is_active ? 'p-ok' : 'p-draft'}`} style={{ marginLeft: 'auto', flexShrink: 0 }}>{viewRow.is_active ? 'Active' : 'Inactive'}</span>
              <button type="button" className="modal-close" onClick={() => setViewRow(null)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
              <div className="vw-grid">
                <div className="fg"><label className="fl">Start of Course</label><div className="vw-val">{formatDate(viewRow.soc_date)}</div></div>
                <div className="fg"><label className="fl">End of Course</label><div className="vw-val">{formatDate(viewRow.eoc_date)}</div></div>
                <div className="fg"><label className="fl">Intake Type</label><div className="vw-val">{viewRow.intake_type || '—'}</div></div>
                <div className="fg"><label className="fl">Capacity</label><div className="vw-val">{viewRow.occupied_count}/{viewRow.capacity} occupied · {viewRow.student_count} active students</div></div>
                <div className="fg"><label className="fl">Min Attendance Rate</label><div className="vw-val">{viewRow.min_attendance_rate ?? '—'}{viewRow.min_attendance_rate != null ? '%' : ''}</div></div>
                <div className="fg"><label className="fl">Min Assessment Result</label><div className="vw-val">{viewRow.min_assessment_result ?? '—'}{viewRow.min_assessment_result != null ? '%' : ''}</div></div>
                <div className="fg fg-full"><label className="fl">Created By</label><div className="vw-val">{viewRow.created_by_name || '—'}</div></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setViewRow(null)}>Close</button>
              {perms?.update && (
                <button type="button" className="btn btn-primary" onClick={() => { setViewRow(null); openEdit(viewRow); }}><i className="fa-solid fa-pen" /> Edit Intake</button>
              )}
            </div>
          </div>
        </div>
      )}

      {modulesRow && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setModulesRow(null)}>
          <div className="card modal" style={{ width: 480, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Modules for {modulesRow.intake_code}</div>
              <button type="button" className="modal-close" onClick={() => setModulesRow(null)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
              {!modules.length && <div style={{ color: 'var(--slate)' }}>Loading modules…</div>}
              {!!modules.length && (
                <>
                  <div className="icon-field fg-tight">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input className="finput" placeholder="Search modules…" value={modulesSearch} onChange={(e) => setModulesSearch(e.target.value)} />
                  </div>
                  {!!checkedModuleObjs.length && (
                    <div className="chip-row">
                      {checkedModuleObjs.map((m) => (
                        <span key={m.module_code} className="chip">
                          <span>{m.module_name}</span>
                          <button type="button" onClick={() => toggleModuleChecked(m.module_code)} aria-label={`Remove ${m.module_name}`}><i className="fa-solid fa-xmark" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.5rem' }}>
                    <button type="button" className="btn btn-ghost btn-sm" disabled={!filteredModulesList.length} onClick={toggleAllFilteredModules}>
                      {allFilteredModulesChecked ? 'Clear' : 'Select'} {modulesSearch ? 'matching' : 'all'}
                    </button>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)' }}>{modulesChecked.size} of {modules.length} selected</span>
                  </div>
                  <div className="chk-list">
                    {!filteredModulesList.length && <div className="chk-empty">No modules match &ldquo;{modulesSearch}&rdquo;.</div>}
                    {filteredModulesList.map((m) => (
                      <label key={m.module_code} htmlFor={`mod-${m.module_code}`} className="chk-row">
                        <input type="checkbox" id={`mod-${m.module_code}`} checked={modulesChecked.has(m.module_code)} onChange={() => toggleModuleChecked(m.module_code)} />
                        <span>{m.module_name} <span style={{ color: 'var(--slate-lt)', fontSize: 'var(--text-xs)' }}>({m.module_code})</span></span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setModulesRow(null)}><i className="fa-solid fa-xmark" /> Cancel</button>
              <button type="button" className="btn btn-primary" disabled={modulesSaving} onClick={saveModules}>
                <i className={`fa-solid ${modulesSaving ? 'fa-spinner fa-spin' : 'fa-check'}`} />
                {modulesSaving ? 'Saving…' : `Save Modules (${modulesChecked.size})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {transferRow && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setTransferRow(null)}>
          <form className="card modal" style={{ width: 440, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()} onSubmit={submitTransfer}>
            <div className="modal-header">
              <div className="modal-title">Transfer Students from {transferRow.intake_code}</div>
              <button type="button" className="modal-close" onClick={() => setTransferRow(null)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
              <div className="fg fg-tight">
                <label className="fl">Destination Intake *</label>
                <SearchableSelect
                  className="finput" required value={transferForm.to_intake_code}
                  onChange={(val) => setTransferForm((f) => ({ ...f, to_intake_code: val }))}
                  placeholder="Select intake"
                  options={transferableIntakes.map((i) => ({
                    value: i.intake_code,
                    label: i.intake_code,
                    sublabel: `${formatDate(i.soc_date)} → ${formatDate(i.eoc_date)}`,
                  }))}
                />
                {!transferableIntakes.length && (
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)', marginTop: '.25rem' }}>No ongoing or upcoming intakes available to transfer into.</div>
                )}
              </div>
              <div className="fg fg-tight"><label className="fl">Transfer Date *</label><input className="finput" type="date" required max={new Date().toISOString().slice(0, 10)} value={transferForm.transfer_date} onChange={(e) => setTransferForm((f) => ({ ...f, transfer_date: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setTransferRow(null)}><i className="fa-solid fa-xmark" /> Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={transferSubmitting}>
                <i className={`fa-solid ${transferSubmitting ? 'fa-spinner fa-spin' : 'fa-right-left'}`} />
                {transferSubmitting ? 'Transferring…' : 'Transfer All Active Students'}
              </button>
            </div>
          </form>
        </div>
      )}

      {bulkModalOpen && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setBulkModalOpen(false)}>
          <div className="card modal" style={{ width: 480, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Assign Modules to {selectedIds.size} Intake(s)</div>
              <button type="button" className="modal-close" onClick={() => setBulkModalOpen(false)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
              {!modules.length && <div style={{ color: 'var(--slate)' }}>Loading modules…</div>}
              {!!modules.length && (
                <>
                  <div className="icon-field fg-tight">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input className="finput" placeholder="Search modules…" value={bulkSearch} onChange={(e) => setBulkSearch(e.target.value)} />
                  </div>
                  {!!checkedBulkModuleObjs.length && (
                    <div className="chip-row">
                      {checkedBulkModuleObjs.map((m) => (
                        <span key={m.module_code} className="chip">
                          <span>{m.module_name}</span>
                          <button type="button" onClick={() => toggleBulkChecked(m.module_code)} aria-label={`Remove ${m.module_name}`}><i className="fa-solid fa-xmark" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.5rem' }}>
                    <button type="button" className="btn btn-ghost btn-sm" disabled={!filteredBulkModulesList.length} onClick={toggleAllFilteredBulkModules}>
                      {allFilteredBulkChecked ? 'Clear' : 'Select'} {bulkSearch ? 'matching' : 'all'}
                    </button>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)' }}>{bulkChecked.size} of {modules.length} selected</span>
                  </div>
                  <div className="chk-list">
                    {!filteredBulkModulesList.length && <div className="chk-empty">No modules match &ldquo;{bulkSearch}&rdquo;.</div>}
                    {filteredBulkModulesList.map((m) => (
                      <label key={m.module_code} htmlFor={`bulk-mod-${m.module_code}`} className="chk-row">
                        <input type="checkbox" id={`bulk-mod-${m.module_code}`} checked={bulkChecked.has(m.module_code)} onChange={() => toggleBulkChecked(m.module_code)} />
                        <span>{m.module_name} <span style={{ color: 'var(--slate-lt)', fontSize: 'var(--text-xs)' }}>({m.module_code})</span></span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setBulkModalOpen(false)}><i className="fa-solid fa-xmark" /> Cancel</button>
              <button type="button" className="btn btn-primary" disabled={bulkSubmitting || !bulkChecked.size} onClick={submitBulkAssign}>
                <i className={`fa-solid ${bulkSubmitting ? 'fa-spinner fa-spin' : 'fa-check'}`} />
                {bulkSubmitting ? 'Assigning…' : `Assign Modules (${bulkChecked.size})`}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Intake"
        message={`Delete intake ${deleteTarget?.intake_code}? This cannot be undone.`}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
